import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {CodingAssistantButton} from '@github-ui/coding-assistant-button'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {noop} from '@github-ui/noop'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {CopyIcon, KebabHorizontalIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useRef, useState} from 'react'

import {BUTTON_LABELS} from '../../constants/buttons'
import type {OptionConfig} from '../OptionConfig'
import {CreateIssueButtonShell} from './CreateIssueButtonShell'
import type {HeaderMenuIssueNode, HeaderMenuRepositoryNode} from './header-menu-query-types'
import styles from './HeaderMenu.module.css'

const CreateIssueButton = React.lazy(async () => {
  const m = await import('@github-ui/issue-create/CreateIssueButton')
  return {default: m.CreateIssueButton}
})

export type HeaderMenuBaseProps = {
  optionConfig: OptionConfig
  setIsIssueTitleEditActive?: (isActive: boolean) => void
  issueTitle?: string
}

// RELAY-CLEANUP(HeaderViewer, HeaderMetadata): widens nullable fields so the still-Relay parents'
// data is assignable. Remove when HeaderViewer and HeaderMetadata leave Relay.
export type HeaderMenuFragment = RelayCompat<
  Pick<HeaderMenuIssueNode, 'number' | 'url'> & {
    readonly repository: Pick<HeaderMenuRepositoryNode, 'name' | 'isArchived' | 'isWritable'> & {
      readonly owner: Pick<HeaderMenuRepositoryNode['owner'], 'login'>
    }
  }
>

type HeaderMenuProps = {
  headerMenuData: HeaderMenuFragment
} & HeaderMenuBaseProps

export function HeaderMenu({
  headerMenuData: {number: issueNumber, url, repository},
  optionConfig: {
    customEditMenuEntries,
    navigate,
    showIssueCreateButton,
    additionalHeaderActions,
    onClose,
    useViewportQueries,
    insideSidePanel,
  },
}: HeaderMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const codingAssistantRef = useRef<HTMLElement | null>(null)
  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  // When the sticky header is enabled, the open-in-agent, copy-link, and create-issue actions render in the sticky top container instead.
  const showInlineActions = !stickyHeaderEnabled
  const showCreateIssueButton = showIssueCreateButton && repository.isWritable && !stickyHeaderEnabled
  // In a side sheet with the sticky header on, the pin/expand, more-actions kebab, and close
  // controls are relocated into the sticky top bar (see StickyHeader). Suppress them here so
  // they don't render twice.
  const relocateSidePanelNav = stickyHeaderEnabled && !!insideSidePanel

  const agentAssignmentEnabled = isFeatureEnabled('issue_cca_modal_open')
  const [modalOpen, setModalOpen] = useState(false)

  const handleOpenModal = useCallback(() => {
    setModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  return (
    <div className={clsx(styles.menuActionsContainer, stickyHeaderEnabled && styles.menuActionsContainer_stickyHeader)}>
      {isFeatureEnabled('issues_open_in_tool_links') && !isFeatureEnabled('issues_sticky_header') && (
        <CodingAssistantButton
          anchorRef={codingAssistantRef}
          className="mr-1"
          url={url}
          buttonLabel="Open in coding assistant"
          promptBuilder={(issueUrl: string) => `Work on the following GitHub issue: ${issueUrl}.`}
          size="medium"
          iconOnly
          onAssignToAgent={agentAssignmentEnabled ? handleOpenModal : undefined}
        />
      )}
      {agentAssignmentEnabled && modalOpen && (
        <CopilotAssignmentModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          repository={repository}
          issueNumbers={[issueNumber]}
          returnFocusRef={codingAssistantRef}
        />
      )}
      <div
        className={clsx(
          styles.buttonContainer,
          useViewportQueries ? styles.buttonContainer_viewport : styles.buttonContainer_container,
        )}
      >
        {showCreateIssueButton && (
          <Suspense fallback={<CreateIssueButtonShell />}>
            <CreateIssueButton
              label={BUTTON_LABELS.new}
              navigate={navigate}
              optionConfig={{
                showRepositoryPicker: false,
                issueCreateArguments: {
                  repository: {
                    owner: repository.owner.login,
                    name: repository.name,
                  },
                },
                showFullScreenButton: true,
                // Only navigate if we're in the Repo#Index (ie, have a scoped repository)
                navigateToFullScreenOnTemplateChoice: navigate !== noop && repository !== null,
                canBypassTemplateSelection: true,
                navigate,
              }}
            />
          </Suspense>
        )}
      </div>
      {showInlineActions && (
        <CopyToClipboardButton
          textToCopy={url}
          ariaLabel={BUTTON_LABELS.copyIssueLink}
          icon={CopyIcon}
          tooltipProps={{direction: 's'}}
        />
      )}
      {!relocateSidePanelNav && additionalHeaderActions}
      {!relocateSidePanelNav && customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              ref={anchorRef}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {!relocateSidePanelNav && onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}

export function StickyHeaderMenu({
  headerMenuData: {url},
  optionConfig: {customEditMenuEntries, additionalHeaderActions, onClose, insideSidePanel},
}: HeaderMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)

  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  // See HeaderMenu: in a side sheet with the sticky header on, these controls live in the
  // sticky top bar, so suppress them here to avoid duplicates.
  const relocateSidePanelNav = stickyHeaderEnabled && !!insideSidePanel

  return (
    <div className={clsx(styles.menuActionsContainer, stickyHeaderEnabled && styles.menuActionsContainer_stickyHeader)}>
      <CopyToClipboardButton
        textToCopy={url}
        ariaLabel={BUTTON_LABELS.copyIssueLink}
        icon={CopyIcon}
        tooltipProps={{direction: 's'}}
      />
      {!relocateSidePanelNav && additionalHeaderActions}
      {!relocateSidePanelNav && customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              tooltipDirection="n"
              ref={anchorRef}
              className={styles.kebabMenuButton}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {!relocateSidePanelNav && onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}
